// Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import '../styles/CSReportPDF.css';
// @ts-ignore
import html2pdf from 'html2pdf.js';
import logo from '../images/wso2-logo-orange.png';
import dayjs, { Dayjs } from 'dayjs';

const generateAgendaPage = (widgets: { selector: string, subTopic?: string }[], pdfContent: HTMLElement) => {
  const agendaPage = document.createElement('div');
  agendaPage.classList.add('agenda-page');

  const agendaTitle = document.createElement('h1');
  agendaTitle.textContent = "Table of Contents";
  agendaPage.appendChild(agendaTitle);

  widgets.forEach(({ selector, subTopic }) => {
    if (subTopic) {
      const agendaItem = document.createElement('p');
      agendaItem.textContent = subTopic; 
      agendaItem.classList.add('agenda-item'); 
      agendaPage.appendChild(agendaItem);
    }
  });

  if (agendaPage.children.length > 1) {
    pdfContent.appendChild(agendaPage); 
  }};

export const CsReportPDF = (contentId: string, account: string, key: string, records : any ,from: Dayjs | null, to: Dayjs | null , notesText: string, notesImage: string, deploymentImage: string,businessOverviewText: string) => {
  const contentElement = document.getElementById(contentId);
  if (!contentElement) {
    console.error(`Element with id "${contentId}" not found.`);
    return;
  }

  const pdfContent = document.createElement('div');

  const frontPage = document.createElement('div');
  frontPage.classList.add('front-page');
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  const formattedDate: string = new Date().toLocaleDateString(undefined, dateOptions);

  frontPage.innerHTML = `
    <img src="${logo}" alt="Logo" class="front-page-logo" />
    <h1 class="header-title">${account} - Customer Success Report </h1>
    <p>Accelerating Success Together</p>
    <hr class="section-divider"/>
    <p class="current-date">Generated on: ${formattedDate}</p>
  `;
  pdfContent.appendChild(frontPage);

  const widgets = [
    { selector: '.business-overview-section', subTopic: 'Client Business Overview' },
    { selector: '.deployment-image-section', subTopic: 'Current Deployment' },
    { selector: '.subscription-widget', subTopic: 'Support Update' },
    { selector: '.current-products-widget' },
    { selector: '.product-versions-widget' },
    { selector: '.cases-charts-widget' },
    { selector: '.cases-volume-widget' }, 
    { selector: '.incident-sla-compliance-widget' },
    { selector: '.product-updates-widget'},
    { selector: '.engagements-widget', subTopic: 'Engagements' },
    { selector: '.extra-notes-section', subTopic: 'Notes' },
  ];

  generateAgendaPage(widgets, pdfContent);

  widgets.forEach(({ selector, subTopic }) => {
    const element = contentElement.querySelector(selector);
    if (element) {
      if (
        (selector === ".extra-notes-section" && !notesText && !notesImage) ||
        (selector === ".deployment-image-section" && !deploymentImage)
      ) {
        return; 
      }

      if (subTopic && selector === '.business-overview-section' && businessOverviewText === "") {
        return;
      }

      if (subTopic) {
        if (element.innerHTML.trim()) { 
          const pageBreak = document.createElement('div');
          pageBreak.classList.add('page-break');
          pdfContent.appendChild(pageBreak);

          const widgetTitlePage = document.createElement('div');
          widgetTitlePage.classList.add('sub-topic-page');
          widgetTitlePage.innerHTML = `
              <h1 class="header-title">${subTopic}</h1>
              <hr class="section-divider"/>
          `;
          pdfContent.appendChild(widgetTitlePage);
        }}

      const widgetContainer = document.createElement("div");
      widgetContainer.classList.add("page-break");

      if (selector === ".cases-charts-widget") {
        widgetContainer.innerHTML = `
          <h2 class="widget-title">Cases </h2>
        `;
        const chartCanvases = element.querySelectorAll("canvas") as NodeListOf<HTMLCanvasElement>;
        const chartsContainer = document.createElement("div");
        chartsContainer.classList.add("charts-container");
        if (chartCanvases.length > 0) {
          chartCanvases.forEach((chartCanvas) => {
        if (chartCanvas) {
          const img = new Image();
          img.src = chartCanvas.toDataURL("image/png");
          chartsContainer.appendChild(img);
        }
          });
        } else {
          chartsContainer.innerHTML = '<p>No data to show</p>';
        }
        widgetContainer.appendChild(chartsContainer);
        pdfContent.appendChild(widgetContainer);

      } else if (selector === ".cases-volume-widget") {
        widgetContainer.innerHTML = `
          <h2 class="widget-title">Case Volume by Quarters</h2>
        `;
        const chartCanvases = element.querySelectorAll("canvas") as NodeListOf<HTMLCanvasElement>;
        const chartsContainer = document.createElement("div");
        chartsContainer.classList.add("trend-chart-container");
        if (chartCanvases.length > 0) {
          chartCanvases.forEach((chartCanvas) => {
        if (chartCanvas) {
          const img = new Image();
          img.src = chartCanvas.toDataURL("image/png");
          chartsContainer.appendChild(img);
        }
          });
        } else {
          chartsContainer.innerHTML = '<p>No data to show</p>';
        }
        widgetContainer.appendChild(chartsContainer);
        widgetContainer.appendChild(chartsContainer);
        pdfContent.appendChild(widgetContainer);

      } else if (selector === ".subscription-widget") {
        const dataContent = element.innerHTML.trim();
        widgetContainer.classList.remove("page-break");
        widgetContainer.innerHTML = `
          <div class="subscription-content">
            <div>${dataContent || '<p>No data to show</p>'}</div>
          </div>
        `;
        pdfContent.appendChild(widgetContainer);

      } else if (selector === ".current-products-widget") {
        const dataContent = element.innerHTML.trim();
        widgetContainer.innerHTML = `
          <div>${dataContent || '<p>No data to show</p>'}</div>
        `;
        pdfContent.appendChild(widgetContainer); 

      } else if (selector === ".product-versions-widget") {
        const dataContent = element.innerHTML.trim();
        widgetContainer.innerHTML = `
          <div class="product-versions-content">
            <div>${dataContent || '<p>No data to show</p>'}</div>
          </div>
        `;
        pdfContent.appendChild(widgetContainer);
      
      } else if (selector === ".incident-sla-compliance-widget") {
        const dataContent = element.innerHTML.trim();
        widgetContainer.innerHTML = `
         <div class="incident-sla-compliance-widget">
            <div>${dataContent || '<p>No data to show</p>'}</div>
          </div>
        `;
        pdfContent.appendChild(widgetContainer);

      } else if (selector === ".product-updates-widget") {
        const dataContent = element.innerHTML.trim();
        widgetContainer.innerHTML = `
          <div>${dataContent || '<p>No data to show</p>'}</div>
        `;
        pdfContent.appendChild(widgetContainer);

      } else if (selector === ".engagements-widget") {
        const dataContent = element.innerHTML.trim();
        widgetContainer.classList.remove("page-break");
        widgetContainer.innerHTML = `
          <div class="engagements-content">
            <div>${dataContent || '<p>No data to show</p>'}</div>
          </div>
        `;
        pdfContent.appendChild(widgetContainer);

      } else if (selector === ".business-overview-section" && businessOverviewText) {
        const title = element.querySelector(".widget-title")?.textContent || "Business Overview";
        const textarea = element.querySelector("textarea") as HTMLTextAreaElement;
        const formattedText = textarea ? textarea.value.replace(/\n/g, "<br>") : "No data to show";
        widgetContainer.innerHTML = `
          <h2 class="widget-title">${title}</h2>
          <p>${formattedText}</p>
        `;
        pdfContent.appendChild(widgetContainer);

      } else if (selector === ".deployment-image-section" ) {
        const title = element.querySelector(".widget-title")?.textContent || "Deployment";
        const imageElement = element.querySelector("img");         

        if (imageElement) {
          const deploymentElement = document.createElement("div");
          deploymentElement.classList.add("styled-text");
          deploymentElement.innerHTML = `
            <h2 class="widget-title">${title}</h2>
          `;

          const imageWrapperDiv = document.createElement("div");
          imageWrapperDiv.style.display = "flex";
          imageWrapperDiv.style.justifyContent = "center";
          imageWrapperDiv.style.alignItems = "center";
          imageWrapperDiv.style.height = "80mm";
          imageWrapperDiv.style.paddingTop = "50mm";

          const clonedImage = imageElement.cloneNode(true) as HTMLElement;
          imageWrapperDiv.appendChild(clonedImage);

          deploymentElement.appendChild(imageWrapperDiv);
          pdfContent.appendChild(deploymentElement);
        }

      } else if (selector === ".extra-notes-section") {
        const title = element.querySelector(".widget-title")?.textContent || "Notes";

        const image = element.querySelector("img") as HTMLImageElement;
        const textarea = element.querySelector("textarea") as HTMLTextAreaElement;
        const formattedText = textarea ? textarea.value.replace(/\n/g, "<br>") : "No data to show";

        const textElement = document.createElement("div");
        textElement.classList.add("styled-text");

        textElement.innerHTML = `
          <h2 class="widget-title">${title}</h2>
          <p>${formattedText}</p>
        `;
        pdfContent.appendChild(textElement);

        if (image) {
            const imageElement = document.createElement("div");
            imageElement.classList.add("page-break");

            imageElement.innerHTML = `
              <h2 class="widget-title">${title}</h2>
            `;

            const imageWrapperDiv = document.createElement("div");
            imageWrapperDiv.style.display = "flex";
            imageWrapperDiv.style.justifyContent = "center";
            imageWrapperDiv.style.alignItems = "center";
            imageWrapperDiv.style.height = "60mm";
            imageWrapperDiv.style.paddingTop = "67mm"; 

            const clonedImage = new Image();
            clonedImage.src = image.src;

            imageWrapperDiv.appendChild(clonedImage);
            imageElement.appendChild(imageWrapperDiv);
            pdfContent.appendChild(imageElement);
          }}}});
  
  const pageBreak = document.createElement('div');
  pageBreak.classList.add('page-break');
  pdfContent.appendChild(pageBreak);
  
  const recordsPage = document.createElement('div');
  recordsPage.classList.add('records-page');
  recordsPage.innerHTML = `
    <h1> SLA & Other Information </h1>
    <hr class="section-divider"/>
  `;
  pdfContent.appendChild(recordsPage);

  const slaTablePage = document.createElement('div');
  slaTablePage.classList.add('sla-table-page');

  const slaTitle = document.createElement('h2');
  slaTitle.classList.add('sla-title');
  slaTitle.textContent = "SLA Report";
  slaTablePage.appendChild(slaTitle);

  const slaTable = document.createElement('table');
  slaTable.classList.add('sla-table');

  if (records.length === 0) {
    slaTable.innerHTML = `
        <thead>
            <tr>
                <th>Task</th>
                <th>SLA Definition</th>
                <th>Business Elapsed Percentage(%)</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td colspan="3" style="text-align: center; color: gray;">No SLA data available</td>
            </tr>
        </tbody>
    `;
} else {
    slaTable.innerHTML = `
        <thead>
            <tr>
                <th>Task</th>
                <th>SLA Definition</th>
                <th>Business Elapsed Percentage(%)</th>
            </tr>
        </thead>
        <tbody>
            ${records.map((record: { task: string, slaDefinition: string, businessElapsedPercentage: string }) => `
                <tr>
                    <td>${record.task}</td>
                    <td>${record.slaDefinition}</td>
                    <td>${record.businessElapsedPercentage}</td>
                </tr>
            `).join('')}
        </tbody>
    `;
}
  
  slaTablePage.appendChild(slaTable);
  pdfContent.appendChild(slaTablePage);

  const pageBreak1 = document.createElement('div');
  pageBreak1.classList.add('page-break');
  pdfContent.appendChild(pageBreak1);

  const thankPage = document.createElement('div');
  thankPage.classList.add('thank-page');
  thankPage.innerHTML = `
    <h1>Thanks!</h1>
    <hr class="section-divider"/>
  `;
  pdfContent.appendChild(thankPage);

  const formattedFrom = from ? dayjs(from).format('YYYY-MM-DD') : 'UnknownStart';
  const formattedTo = to ? dayjs(to).format('YYYY-MM-DD') : 'UnknownEnd';
  const dateRange = `${formattedFrom}_to_${formattedTo}`;

  const options = {
    margin: [0, 0, 0, 0],
    filename: `CS_Report_${key}_${dateRange}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    pagebreak: { mode: ['avoid-all', 'css'] },
    html2canvas: { scale: 3, scrollY: 0, allowTaint: true, useCORS: true ,backgroundColor: null},
    jsPDF: { unit: 'mm', format: 'a5', orientation: 'landscape' },
  };

  html2pdf().from(pdfContent).set(options).save();
};
